import React, { useRef, useState, useCallback } from 'react';
import { validateFile, formatFileSize } from '../utils/validation';
import { LoadingSpinner } from './Layout';

interface FileUploadZoneProps {
  onFileSelect: (file: File) => void;
  selectedFile: File | null;
  isProcessing?: boolean;
  error?: string | null;
  className?: string;
}

export const FileUploadZone: React.FC<FileUploadZoneProps> = ({
  onFileSelect,
  selectedFile,
  isProcessing = false,
  error,
  className = '',
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [, setDragCounter] = useState(0);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev + 1);
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => {
      const newCount = prev - 1;
      if (newCount === 0) {
        setIsDragOver(false);
      }
      return newCount;
    });
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    setDragCounter(0);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelection(files[0]);
    }
  }, []);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelection(files[0]);
    }
  }, []);

  const handleFileSelection = useCallback((file: File) => {
    const validation = validateFile(file);
    if (validation.valid) {
      onFileSelect(file);
    }
  }, [onFileSelect]);

  const handleClick = useCallback(() => {
    if (!isProcessing && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [isProcessing]);

  const handleRemoveFile = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onFileSelect(null as any); // Reset file selection
  }, [onFileSelect]);


  const getUploadIcon = () => (
    <svg className="w-12 h-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
    </svg>
  );

  const baseClasses = `
    relative border-2 border-dashed rounded-xl transition-all duration-200 ease-in-out
    ${isProcessing ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:border-emerald-400'}
    ${isDragOver ? 'border-emerald-500 bg-emerald-50' : 'border-slate-300'}
    ${error ? 'border-red-300 bg-red-50' : ''}
  `;

  return (
    <div className={`${baseClasses} ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf"
        onChange={handleFileInputChange}
        className="hidden"
        disabled={isProcessing}
        aria-label="Upload PDF file"
      />
      
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleClick}
        className="px-8 py-14 text-center"
        role="button"
        tabIndex={isProcessing ? -1 : 0}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !isProcessing) {
            e.preventDefault();
            handleClick();
          }
        }}
        aria-label="File upload area"
      >
        {selectedFile ? (
          <FilePreview 
            file={selectedFile} 
            onRemove={handleRemoveFile}
            isProcessing={isProcessing}
          />
        ) : (
          <div className="space-y-5">
            <div className="flex justify-center">
              {isDragOver ? (
                <div className="animate-bounce">
                  {getUploadIcon()}
                </div>
              ) : (
                getUploadIcon()
              )}
            </div>

            <div className="space-y-3">
              <h3 className="text-2xl font-semibold text-slate-900">
                {isDragOver ? 'Drop your PDF here' : 'Understand Your State Notice'}
              </h3>
              <p className="text-base text-slate-600">
                Turn a government notice into an action plan — summary, priority, and next steps.
              </p>
            </div>

            <div className="pt-2">
              <span className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors">
                Select PDF to upload
              </span>
              <p className="text-xs text-slate-400 mt-3">or drag and drop &middot; 50MB max</p>
              <p className="text-xs text-slate-600 mt-2"><span className="text-red-500">*</span> Please redact SSNs, EINs, home addresses, and any other sensitive information before uploading.</p>
            </div>
          </div>
        )}
        
        {isProcessing && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-xl">
            <div className="text-center space-y-3">
              <LoadingSpinner size="lg" className="text-emerald-600" />
              <p className="text-sm font-medium text-slate-700">Processing document...</p>
            </div>
          </div>
        )}
      </div>
      
      {error && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}
    </div>
  );
};

interface FilePreviewProps {
  file: File;
  onRemove: (e: React.MouseEvent) => void;
  isProcessing: boolean;
}

const FilePreview: React.FC<FilePreviewProps> = ({ file, onRemove, isProcessing }) => {
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  return (
    <div className="space-y-4 w-full" onClick={(e) => e.stopPropagation()}>
      {/* File info bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 bg-red-100 rounded flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{file.name}</p>
            <p className="text-xs text-slate-500">{formatFileSize(file.size)}</p>
          </div>
        </div>
        {!isProcessing && (
          <button
            onClick={onRemove}
            className="text-slate-400 hover:text-red-500 transition-colors p-1.5 rounded-md hover:bg-slate-100"
            aria-label="Remove file"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* PDF preview */}
      {previewUrl && (
        <div className="rounded-lg overflow-hidden border border-slate-200 bg-slate-100">
          <iframe
            src={`${previewUrl}#toolbar=0&navpanes=0`}
            title="PDF preview"
            className="w-full h-[360px]"
          />
        </div>
      )}
    </div>
  );
};